/**
 * components/Onboarding/modals/CompletionSuccessModal.tsx
 * Success modal shown after onboarding completion
 *
 * Displays a celebration message with animation before redirecting to dashboard
 */

// 1. External dependencies
import { CheckCircle } from 'lucide-react';

// 2. Internal absolute imports
import { Modal, ModalContent, ModalHeader, ModalBody, Button } from '@enfomentis/ui';

/**
 * Props for CompletionSuccessModal
 */
interface CompletionSuccessModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Callback when user clicks "Go to Dashboard" */
  onGoToDashboard: () => void;
}

/**
 * Completion success modal component
 *
 * Shows animated success message after onboarding completion
 */
export const CompletionSuccessModal: React.FC<CompletionSuccessModalProps> = ({
  isOpen,
  onGoToDashboard
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={() => {}} // Prevent closing - must click button
      isDismissable={false}
      hideCloseButton={true}
      size="lg"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center justify-center">
            <div className="relative">
              {/* Animated success icon */}
              <div className="animate-bounce">
                <CheckCircle className="w-20 h-20 text-success" strokeWidth={2} />
              </div>
              {/* Pulse ring effect */}
              <div className="absolute inset-0 rounded-full bg-success opacity-20 animate-ping"></div>
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="text-center pb-8">
          <h2 className="text-2xl font-bold text-text-primary mb-3">
            Setup Complete!
          </h2>
          <p className="text-text-secondary mb-6 text-base">
            Your trading journal is ready to use. Let's start tracking your trades and building better habits.
          </p>
          <Button
            color="primary"
            size="lg"
            className="w-full"
            onClick={onGoToDashboard}
          >
            Go to Dashboard
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
